import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import UserService from '../features/profile/services/UserService'
import { useAuth } from './AuthContext'

const HelpAssistantContext = createContext()

export const useHelpAssistantContext = () => {
  const context = useContext(HelpAssistantContext)
  if (!context) {
    throw new Error(
      'useHelpAssistantContext debe usarse dentro de un HelpAssistantProvider',
    )
  }
  return context
}

export const HelpAssistantProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  // Estado principal
  const [state, setState] = useState({
    isOpen: false,
    isDismissed: false,
    currentContext: '',
    suggestions: [],
    hasUnreadHelp: false,
    lastSeenContext: '',
    completedTours: [],
    feedbackSubmitted: false,
    // Tour state
    currentTourId: null,
    currentStepIndex: 0,
    tourSteps: [],
  })

  const backendLoaded = useRef(false)

  // Preferences fetched via React Query — shared key deduplicates across contexts
  const { data: prefs } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => UserService.getPreferences().catch(() => null),
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
    retry: false,
  })

  // Cargar estado persistente desde backend, con fallback a localStorage
  useEffect(() => {
    if (!prefs) return
    if (prefs.helpAssistantState) {
      const parsed = JSON.parse(prefs.helpAssistantState)
      setState((prev) => ({
        ...prev,
        isDismissed: parsed.isDismissed || false,
        completedTours: parsed.completedTours || [],
        lastSeenContext: parsed.lastSeenContext || '',
        feedbackSubmitted: parsed.feedbackSubmitted || false,
      }))
      backendLoaded.current = true
      return
    }
    // Fallback to localStorage
    try {
      const saved = localStorage.getItem('helpAssistantState')
      if (saved) {
        const parsed = JSON.parse(saved)
        setState((prev) => ({
          ...prev,
          isDismissed: parsed.isDismissed || false,
          completedTours: parsed.completedTours || [],
          lastSeenContext: parsed.lastSeenContext || '',
          feedbackSubmitted: parsed.feedbackSubmitted || false,
        }))
      }
    } catch (err) {
      console.warn('Error cargando estado de ayuda:', err)
    }
  }, [prefs])

  // Guardar estado persistente en localStorage y backend cuando cambie
  useEffect(() => {
    const { isOpen, suggestions, hasUnreadHelp, ...persistentState } = state
    localStorage.setItem('helpAssistantState', JSON.stringify(persistentState))
    if (backendLoaded.current) {
      UserService.updatePreferences({
        helpAssistantState: JSON.stringify(persistentState),
      }).catch((err) =>
        console.warn('Error guardando estado de ayuda en backend:', err),
      )
    }
  }, [state])

  // Acciones
  const open = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
      isDismissed: false,
      hasUnreadHelp: false,
    }))
  }, [])

  const close = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }, [])

  const dismiss = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isDismissed: true,
      isOpen: false,
    }))
  }, [])

  const setSuggestions = useCallback((suggestions) => {
    setState((prev) => ({
      ...prev,
      suggestions,
      hasUnreadHelp: suggestions.length > 0 && !prev.isOpen,
    }))
  }, [])

  const setCurrentContext = useCallback((context) => {
    setState((prev) => ({
      ...prev,
      currentContext: context,
      lastSeenContext: context,
    }))
  }, [])

  const markTourCompleted = useCallback((tourId) => {
    setState((prev) => ({
      ...prev,
      completedTours: [...prev.completedTours, tourId],
    }))
  }, [])

  const startTour = useCallback((tourId, steps) => {
    setState((prev) => ({
      ...prev,
      currentTourId: tourId,
      currentStepIndex: 0,
      tourSteps: steps,
    }))
  }, [])

  const nextStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStepIndex < prev.tourSteps.length - 1) {
        return {
          ...prev,
          currentStepIndex: prev.currentStepIndex + 1,
        }
      }
      // End of tour
      return {
        ...prev,
        currentTourId: null,
        currentStepIndex: 0,
        tourSteps: [],
      }
    })
  }, [])

  const prevStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStepIndex > 0) {
        return {
          ...prev,
          currentStepIndex: prev.currentStepIndex - 1,
        }
      }
      return prev
    })
  }, [])

  const endTour = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentTourId: null,
      currentStepIndex: 0,
      tourSteps: [],
    }))
  }, [])

  const markFeedbackSubmitted = useCallback(() => {
    setState((prev) => ({
      ...prev,
      feedbackSubmitted: true,
    }))
  }, [])

  const resetFeedbackSubmitted = useCallback(() => {
    setState((prev) => ({
      ...prev,
      feedbackSubmitted: false,
    }))
  }, [])

  const contextValue = {
    state,
    open,
    close,
    dismiss,
    setSuggestions,
    setCurrentContext,
    markTourCompleted,
    startTour,
    nextStep,
    prevStep,
    endTour,
    markFeedbackSubmitted,
    resetFeedbackSubmitted,
  }

  return (
    <HelpAssistantContext.Provider value={contextValue}>
      {children}
    </HelpAssistantContext.Provider>
  )
}
